import { isSameDay } from 'date-fns';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { useUser } from 'shared/hooks/use-user';
import { Task, UpdateTask } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getDateFormat } from 'shared/utils/get-date-format';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export type DeadlineDeadlineSubMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  tasks: Task[];
  onClose: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

const datePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.Friday,
];

export const DeadlineDeadlineSubMenu: React.FC<
  DeadlineDeadlineSubMenuProps
> = ({ tasks, onClose, onUpdate, ...rest }) => {
  const { submit: updateTasks } = useUpdateTasks();
  const user = useUser();
  const dateFormat = getDateFormat(user);
  const weekStartsOn = getWeekStartsOn(user);

  // Determine initial value: show value only if all tasks share the same deadlineDeadline
  const value = useMemo<Timestamp | undefined>(() => {
    const firstTask = tasks?.[0];
    const firstValue = firstTask?.deadlineDeadline;

    if (!firstValue) {
      return undefined;
    }

    // Check if all tasks have the same value
    const allSame = tasks.every(
      (task) =>
        !task.deadlineDeadline || isSameDay(task.deadlineDeadline, firstValue),
    );

    return allSame ? firstValue : undefined;
  }, [tasks]);

  const onChange = (date?: Timestamp) => {
    if (onUpdate) {
      tasks.forEach((task) =>
        onUpdate(task, { id: task.id, deadlineDeadline: date ?? null }),
      );
      onClose();
      return;
    }

    updateTasks(
      tasks.map((task) => ({
        id: task.id,
        deadlineDeadline: date ?? null,
      })),
    );
    onClose();
  };

  return (
    <DatepickerWithOptions
      {...rest}
      value={value}
      onChange={onChange}
      dateFormat={dateFormat}
      weekStartsOn={weekStartsOn}
      datePresets={datePresets}
      icon={IconOptions.Flag}
      onOutside={onClose}
      canRemoveValue
      forceRemoveValueOption
    />
  );
};
